import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatbotComponent } from '@components';
import { ChatService, UserService } from '@api-services';
import { AuthenticationService } from '../../../services/auth.service';
import { UserDto, ChatDto } from '@api-contracts';

@Component({
  selector: 'wun-backoffice-messages',
  standalone: true,
  imports: [CommonModule, ChatbotComponent, FormsModule],
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss'
})
export class BackofficeMessagesComponent {
  private readonly chatService = inject(ChatService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthenticationService);

  // Usa direttamente il profilo caricato dall'auth service (id è un number corretto)
  currentUserId = computed(() => this.authService.current()?.id);

  selectedUser = signal<UserDto | null>(null);
  activeChatId = signal<number | undefined>(undefined);
  isOpeningChat = signal(false);
  searchQuery = signal('');

  allUsers = signal<UserDto[]>([]);
  existingChats = signal<ChatDto[]>([]);
  isLoadingUsers = signal(false);

  filteredUsers = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const adminId = this.currentUserId();
    const list = this.allUsers().filter(u => u.id !== adminId);
    if (!q) return list;
    return list.filter(u =>
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
  });

  hasExistingChat = computed(() => {
    const chats = this.existingChats();
    const adminId = this.currentUserId();
    const map = new Set<number>();
    for (const c of chats) {
      if (c.fromUser.id === adminId) map.add(c.toUser.id);
      else if (c.toUser.id === adminId) map.add(c.fromUser.id);
    }
    return map;
  });

  constructor() {
    this.loadUsers();
    this.loadChats();
  }

  private loadUsers() {
    this.isLoadingUsers.set(true);
    this.userService.usersFindAndPaginate({
      pageSize: 1000,
      sorting: JSON.stringify({ firstName: 'ASC' })
    }).subscribe({
      next: (res) => {
        this.allUsers.set(res.data ?? []);
        this.isLoadingUsers.set(false);
      },
      error: () => this.isLoadingUsers.set(false)
    });
  }

  private loadChats() {
    this.chatService.chatsFindAndPaginate({
      includes: JSON.stringify(['fromUser', 'toUser']),
      pageSize: 1000,
      sorting: JSON.stringify({ updatedAt: 'DESC' })
    }).subscribe({
      next: (res) => this.existingChats.set(res.data ?? [])
    });
  }

  selectUser(user: UserDto) {
    this.selectedUser.set(user);
    this.activeChatId.set(undefined);
    this.isOpeningChat.set(true);

    const adminId = this.currentUserId()!;
    const existing = this.existingChats().find(c =>
      (c.fromUser.id === adminId && c.toUser.id === user.id) ||
      (c.fromUser.id === user.id && c.toUser.id === adminId)
    );

    if (existing) {
      this.activeChatId.set(existing.id);
      this.isOpeningChat.set(false);
    } else {
      this.chatService.chatsCreate({
        createChatRequestDto: { fromUser: adminId, toUser: user.id }
      }).subscribe({
        next: (res) => {
          this.activeChatId.set(res.data.id);
          this.existingChats.update(list => [...list, res.data]);
          this.isOpeningChat.set(false);
        },
        error: () => this.isOpeningChat.set(false)
      });
    }
  }
}
